import { defaultConfig, plugin } from '@formkit/vue'
import { config as formKitConfig } from '@webapp2/shared/formkit.config'
import type { EntityDict } from '@webapp2/shared/model'
import { configurePrimeVue } from '@webapp2/shared/primevue'
import { createApp } from 'vue'
import { adminState } from './admin/state'
import App from './App.vue'
import { appInsights } from './appinsights'
import { initBackgroundSW } from './background-update'
import Button from './components/Button.vue'
import { router } from './router'
import { adminCache, cache } from './services/cache'
import * as logRocketService from './services/log-rocket-service'
import type { StartupResponseData } from './services/quest-bridge'
import { sessionRefresher } from './services/session-refresher'
import { type BuildDownloadStatus, type VuplexData } from './state'

declare let LOCAL_SETTINGS: LocalSettings | undefined
export type LocalAuth = {
  enabled: boolean
  email: string
  password: string
  redirectPath: string
}
export type LocalSettings = {
  enabled: boolean
  vuplex?: VuplexData & {
    enabled: boolean
    deleteCookieWhenDisabled: boolean
    emulateDownloadProgress: boolean
    emulateQrCode: string
    emulatedStartupResponse?: StartupResponseData
    emulateBuildDownloadStatuses: EntityDict<BuildDownloadStatus>
    playerBuildId: string
  }
  enableHealthStreamLogin: boolean
  auth?: LocalAuth
}

function getLocalSettings() {
  const localStorageSettingsJson = localStorage.getItem('config')
  const localStorageSettings = localStorageSettingsJson
    ? (JSON.parse(localStorageSettingsJson) as LocalSettings)
    : undefined
  const localSettings = navigator.webdriver ? undefined : (localStorageSettings ?? LOCAL_SETTINGS)
  if (localSettings) {
    localSettings.vuplex = localSettings.enabled && localSettings.vuplex?.enabled ? localSettings.vuplex : undefined
  }
  return localSettings
}

export const localSettings = getLocalSettings()

// use config for variables that need changed per environment in the build pipeline, but allow localSettings to override for testing purposes
export type Config = {
  enableHealthStreamLogin: boolean
  logRocketAppId: string
  qrRefreshPeriodInMilliseconds: number
  signalrUrl: string
  legacyAppUri: string
  appInsightsKey: string
  cdnUri: string
}

const fiveMinsInMilliseconds = 5 * 60 * 1000
export const config: Config = {
  enableHealthStreamLogin:
    (window.ossoConfig?.enableHealthStreamLogin?.toLocaleLowerCase() === 'true' ||
      navigator.webdriver ||
      localSettings?.enableHealthStreamLogin) ??
    false,
  logRocketAppId: window.ossoConfig?.logRocketAppId ?? 'cqlomr/ossovr-devtest',
  qrRefreshPeriodInMilliseconds: window.testConfig?.qrRefreshPeriodInMilliseconds ?? fiveMinsInMilliseconds,
  signalrUrl: window.ossoConfig?.signalrUrl ?? 'https://master-qa4cfd17d7.service.signalr.net',
  legacyAppUri: window.ossoConfig?.legacyAppUri ?? 'http://localhost:3000',
  appInsightsKey: window.ossoConfig?.appInsightsKey ?? '',
  cdnUri: window.ossoConfig?.cdnUri ?? 'https://master-qa-static.azureedge.net/'
}

if (navigator.webdriver) {
  window.adminState = adminState
}

if (localSettings) {
  console.log('LOCAL_SETTINGS', LOCAL_SETTINGS)
  console.log('config', config)
}

if (window.testCachedEntities) {
  await cache.setItems(window.testCachedEntities)
}
if (window.testAdminCachedEntities) {
  await adminCache.setItems(window.testAdminCachedEntities)
}

export const updateGlobalServiceWorker = initBackgroundSW()
logRocketService.initialize()
sessionRefresher()

const app = createApp(App).use(router).use(plugin, defaultConfig(formKitConfig)).component('Button', Button)
configurePrimeVue(app)
app.config.errorHandler = (error, vm, info) => {
  console.error(error, vm, info)
  const exception = error instanceof Error ? error : new Error(String(error))
  const component = vm?.$options.__name || 'Unknown Component'
  const file = vm?.$options?.__file || 'Unknown File'
  appInsights.trackException({ exception, properties: { vueInfo: info, component, file } })
}
app.mount('#app')
